/**
 * RES-43 — Consolidado de stock insumo × local (épica D1).
 *
 * El backend arma la matriz (`GET /inventory/stock/consolidated`); acá solo se filtra y ordena en
 * el cliente para que el buscador responda al instante sin pedir de nuevo.
 */

/** Estados de stock de un local, del peor al mejor. */
export const STOCK_STATUSES = ['negative', 'out', 'low', 'ok'];

/** Filtro de estado: `shortage` = cualquier local que no esté `ok`. */
export const STATUS_FILTERS = ['all', 'shortage', 'negative', 'out', 'low', 'ok'];

const SHORTAGE_STATUSES = ['negative', 'out', 'low'];

const statusRank = (status) => {
  const index = STOCK_STATUSES.indexOf(status);
  return index === -1 ? STOCK_STATUSES.length : index;
};

/**
 * Celdas de un insumo por local, en el orden de las columnas.
 * @param {Object} item - Fila del consolidado
 * @param {Array<{restaurantId: string}>} restaurants - Columnas
 * @returns {Array<Object|null>} Celda de cada local o `null` si el local no tiene fila
 */
export function itemCells(item, restaurants) {
  const byRestaurant = (item && item.byRestaurant) || {};
  return (restaurants || []).map((r) => byRestaurant[r.restaurantId] || null);
}

/**
 * Cantidad de locales con faltante (stock bajo, sin stock o negativo) de un insumo.
 * @param {Object} item - Fila del consolidado
 * @returns {number} Locales en falta
 */
export function shortageCount(item) {
  if (item && item.total && Number.isFinite(Number(item.total.shortages))) {
    return Number(item.total.shortages);
  }
  return Object.values((item && item.byRestaurant) || {}).filter((cell) =>
    SHORTAGE_STATUSES.includes(cell && cell.status)
  ).length;
}

/**
 * Peor estado del insumo entre todos los locales.
 * @param {Object} item - Fila del consolidado
 * @returns {string|null} Estado o `null` si no hay celdas
 */
export function worstStatus(item) {
  if (item && item.total && item.total.worstStatus) return item.total.worstStatus;
  const statuses = Object.values((item && item.byRestaurant) || {})
    .map((cell) => cell && cell.status)
    .filter(Boolean);
  if (statuses.length === 0) return null;
  return statuses.sort((a, b) => statusRank(a) - statusRank(b))[0];
}

const normalize = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/**
 * Filtra las filas por texto (nombre o SKU), categoría y estado.
 * @param {Array<Object>} items - Filas del consolidado
 * @param {{search?: string, category?: string, status?: string}} filters - Filtros
 * @returns {Array<Object>} Filas que pasan
 */
export function filterConsolidatedItems(
  items,
  { search = '', category = '', status = 'all' } = {}
) {
  const query = normalize(search);
  return (items || []).filter((item) => {
    if (query && !normalize(item.name).includes(query) && !normalize(item.sku).includes(query)) {
      return false;
    }
    if (category && item.category !== category) return false;
    if (status && status !== 'all') {
      const statuses = Object.values(item.byRestaurant || {}).map((cell) => cell && cell.status);
      if (status === 'shortage') return statuses.some((s) => SHORTAGE_STATUSES.includes(s));
      return statuses.includes(status);
    }
    return true;
  });
}

/**
 * Ordena por nombre o por faltantes (más locales en falta primero, después el peor estado).
 * @param {Array<Object>} items - Filas
 * @param {'name'|'shortages'} sort - Orden
 * @returns {Array<Object>} Copia ordenada
 */
export function sortConsolidatedItems(items, sort = 'name') {
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es');
  const copy = [...(items || [])];
  if (sort !== 'shortages') return copy.sort(byName);
  return copy.sort(
    (a, b) =>
      shortageCount(b) - shortageCount(a) ||
      statusRank(worstStatus(a)) - statusRank(worstStatus(b)) ||
      byName(a, b)
  );
}

/**
 * Local desde el que conviene transferir a `targetRestaurantId`: el que tenga estado `ok` y más
 * stock. `null` si ninguno está bien.
 * @param {Object} item - Fila del consolidado
 * @param {string} targetRestaurantId - Local en falta
 * @returns {string|null} Local origen sugerido
 */
export function suggestTransferSource(item, targetRestaurantId) {
  const candidates = Object.entries((item && item.byRestaurant) || {})
    .filter(([restaurantId, cell]) => restaurantId !== targetRestaurantId && cell?.status === 'ok')
    .sort(([, a], [, b]) => (Number(b.stock) || 0) - (Number(a.stock) || 0));
  return candidates.length ? candidates[0][0] : null;
}

/**
 * Local en falta al que apunta el botón "Transferir" de la fila: el peor estado primero.
 * @param {Object} item - Fila del consolidado
 * @param {Array<{restaurantId: string}>} restaurants - Columnas (para desempatar por orden)
 * @returns {string|null} Local destino sugerido
 */
export function suggestTransferTarget(item, restaurants) {
  const order = (restaurants || []).map((r) => r.restaurantId);
  const candidates = Object.entries((item && item.byRestaurant) || {})
    .filter(([, cell]) => SHORTAGE_STATUSES.includes(cell && cell.status))
    .sort(
      ([idA, a], [idB, b]) =>
        statusRank(a.status) - statusRank(b.status) || order.indexOf(idA) - order.indexOf(idB)
    );
  return candidates.length ? candidates[0][0] : null;
}

/**
 * Stock de cada local por insumo, en la forma que usa `TransferStockModal`.
 * @param {Object} consolidated - Respuesta del consolidado
 * @returns {Object<string, Object<string, number>>} `{ [restaurantId]: { [itemId]: stock } }`
 */
export function stockMapFromConsolidated(consolidated) {
  const map = {};
  ((consolidated && consolidated.items) || []).forEach((item) => {
    Object.entries(item.byRestaurant || {}).forEach(([restaurantId, cell]) => {
      if (!map[restaurantId]) map[restaurantId] = {};
      map[restaurantId][item.itemId] = Number(cell && cell.stock) || 0;
    });
  });
  return map;
}

/**
 * Número corto para la matriz (hasta 2 decimales, sin ceros de más).
 * @param {number} value - Cantidad
 * @returns {string} Texto
 */
export function formatStock(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return String(Math.round(number * 100) / 100);
}
